import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { ArticleOut } from "@/api/client";

type SortKey = "source_name" | "title" | "summary" | "relevance_score" | "importance_score" | "trust_score";
type SortDir = "asc" | "desc";

export function ArticlesTable({
  articles,
  dateLabel,
  loading,
}: {
  articles: ArticleOut[];
  dateLabel: string | null;
  loading: boolean;
}) {
  const [sortKey, setSortKey] = useState<SortKey>("importance_score");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const sorted = useMemo(() => {
    const arr = [...articles];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      let cmp: number;
      if (av === null || av === undefined) cmp = bv === null || bv === undefined ? 0 : 1;
      else if (bv === null || bv === undefined) cmp = -1;
      else if (typeof av === "string" && typeof bv === "string") cmp = av.localeCompare(bv);
      else cmp = (av as number) - (bv as number);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [articles, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir("desc");
    }
  }

  return (
    <div className="md:flex-1 md:overflow-y-auto">
      <div className="px-3 py-1.5 border-b border-border bg-muted/20 text-[11px] flex items-center justify-between sticky top-0 z-10">
        <div className="font-mono text-muted-foreground">
          {dateLabel ? (
            <>
              ARTICLES · <span className="text-foreground">{dateLabel}</span> ·{" "}
              {articles.length} found
            </>
          ) : (
            "ARTICLES"
          )}
        </div>
      </div>
      {loading ? (
        <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">Loading…</div>
      ) : sorted.length === 0 ? (
        <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
          {dateLabel
            ? "No articles found for this date."
            : "Click a date on the chart to see articles from that day."}
        </div>
      ) : (
        <>
          <div className="md:hidden">
            {sorted.map((a) => (
              <ArticleMobileCard key={a.id} a={a} />
            ))}
          </div>
          <Table className="hidden md:table text-[11px]">
            <TableHeader>
              <TableRow>
                <TH col="source_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} width={140}>
                  Origin
                </TH>
                <TH col="title" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>
                  Title
                </TH>
                <TH col="summary" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>
                  Summary
                </TH>
                <TH col="relevance_score" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} width={80}>
                  Relevance
                </TH>
                <TH col="importance_score" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} width={80}>
                  Importance
                </TH>
                <TH col="trust_score" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} width={60}>
                  Trust
                </TH>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((a) => (
                <TableRow key={a.id} className="hover:bg-accent/40">
                  <TableCell className="py-1.5 align-top whitespace-nowrap text-muted-foreground">
                    {a.source_name}
                  </TableCell>
                  <TableCell className="py-1.5 align-top font-medium">{a.title ?? "—"}</TableCell>
                  <TableCell className="py-1.5 align-top text-muted-foreground max-w-[400px] truncate">
                    {a.summary ?? "—"}
                  </TableCell>
                  <TableCell className="py-1.5 align-top">
                    <ScoreBar v={a.relevance_score} />
                  </TableCell>
                  <TableCell className="py-1.5 align-top">
                    <ScoreBar v={a.importance_score} />
                  </TableCell>
                  <TableCell className="py-1.5 align-top font-mono tabular-nums">
                    {a.trust_score ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
}

function TH({
  col,
  sortKey,
  sortDir,
  onSort,
  width,
  children,
}: {
  col: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (k: SortKey) => void;
  width?: number;
  children: React.ReactNode;
}) {
  return (
    <TableHead
      style={{ width }}
      onClick={() => onSort(col)}
      className={cn(
        "h-7 px-2 cursor-pointer text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground bg-muted/30",
      )}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortKey === col &&
          (sortDir === "asc" ? <ArrowUp className="h-2.5 w-2.5" /> : <ArrowDown className="h-2.5 w-2.5" />)}
      </span>
    </TableHead>
  );
}

function ArticleMobileCard({ a }: { a: ArticleOut }) {
  return (
    <div className="block px-3 py-2 border-b border-border/60 min-h-[44px] hover:bg-accent/40">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[12px] font-medium leading-snug min-w-0 truncate">
          {a.title ?? "—"}
        </div>
        <div className="font-mono text-[10px] tabular-nums text-muted-foreground shrink-0">
          trust {a.trust_score ?? "—"}
        </div>
      </div>
      <div className="mt-0.5 flex items-baseline justify-between gap-2 text-[10px] text-muted-foreground font-mono">
        <span className="truncate uppercase tracking-wider">{a.source_name}</span>
        <span className="shrink-0">
          rel {a.relevance_score ?? "—"} · imp {a.importance_score ?? "—"}
        </span>
      </div>
      {a.summary && (
        <div className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{a.summary}</div>
      )}
    </div>
  );
}

export function ScoreBar({ v }: { v: number | null | undefined }) {
  if (v === null || v === undefined) {
    return <span className="text-[10px] text-muted-foreground">—</span>;
  }
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-12 h-1 bg-muted rounded-sm overflow-hidden">
        <div
          className="h-full bg-foreground/70"
          style={{ width: `${Math.min(100, Math.max(0, v))}%` }}
        />
      </div>
      <span className="text-[10px] font-mono tabular-nums">{v}</span>
    </div>
  );
}
